<template>
  <div class="ofs-area ofs__content">
    <div class="ofs__block ofs-area__tree">
      <UIPreloader
        v-if="!initialized"
        message="Loading tree..."
        class="ofs__preloader"
      />
      <UITree
        v-else-if="data.tree"
        v-model:expanded-paths="expandedPaths"
        :data="data.tree"
        :root="AnalyticsField.REPO_ROOT"
        :meta-fields="METADATA_FIELDS"
        :formatter="nameFormatter"
        placeholder="Search accounts..."
      >
        <template #name="{ isRoot, formattedName, path, open }">
          <draggable
            :group="isRoot ? { name: 'nodes', put: true, pull: false } : 'nodes'"
            :model-value="isRoot ? [repoName] : [path]"
            tag="div"
            :animation="0"
            :item-key="(element: string | string[]) => isRoot ? element : (element as string[]).join('-')"
            :scroll="scrollContainer"
            :scroll-sensitivity="250"
            :scroll-speed="50"
            :bubble-scroll="true"
            @choose="handleChoose(path)"
            @change="handleChange($event, path, open)"
            @start="isRemoveAreaVisible = true"
            @end="isRemoveAreaVisible = false"
            @ready="scrollContainer = $event"
          >
            <template #header>
              <div class="ofs-area__tree-item-placeholder">
                {{ formattedName }}
              </div>
            </template>
            <template #item>
              <div class="ofs-area__tree-item-name">
                {{ isRoot ? repoName : formattedName }}
              </div>
            </template>
          </draggable>
        </template>
        <template #meta="{ isRoot, amount, path }">
          <AmountField
            v-if="!isRoot"
            :entry-settings
            :amount
            :dynamic="path.length > 2"
            @click="handleClickAmount(path)"
          />
        </template>
        <template #actions="{ isRoot, path }">
          <div
            v-if="isRoot"
            class="ofs-area__tree-actions"
          >
            <UIButton
              v-tooltip.top="'Undo'"
              variant="gray"
              fill="light"
              size="xs"
              :leading="ArrowUturnLeftIcon"
              :disabled="!canUndo"
              @click="handleUndo"
            />
            <UIButton
              v-tooltip.top="'Redo'"
              variant="gray"
              fill="light"
              size="xs"
              :leading="ArrowUturnRightIcon"
              :disabled="!canRedo"
              @click="handleRedo"
            />
          </div>
          <MoveTo
            v-else
            :data="listData"
            :path
            @select="(value: string) => handleMoveTo(value, path)"
          />
        </template>
      </UITree>
    </div>
  </div>
  <div class="ofs__content">
    <div class="ofs__block ofs-area__unorganized">
      <div class="ofs__header">
        <span>Unorganized owners/groups:</span>
        <UIButton
          label="Owner/Group"
          variant="secondary"
          size="xs"
          :leading="PlusIcon"
          :disabled="!initialized"
          @click="handleAddGroup"
        />
      </div>
      <UIPreloader
        v-if="!initialized"
        message="Loading tree..."
        class="ofs__preloader"
      />
      <div
        v-else
        class="ofs-area__unorganized-area"
      >
        <draggable
          group="nodes"
          :model-value="data.orphans"
          tag="div" 
          handle=".handle"
          :animation="0"
          :item-key="(element: string) => element"
          class="ofs-area__unorganized-list"
          :class="{ 'ofs-area__unorganized-list--no-data': !data.orphans.length && !isRemoveAreaVisible }"
          @change="handleOrphansChange"
        >
          <template #header>
            <div
              v-if="isRemoveAreaVisible"
              class="ofs-area__unorganized-remove-area"
            >
              Move here to remove tree node
            </div>
            <UILayoutNoData
              v-else-if="!data.orphans.length"
              hide-question-icon
              inline
              class="ofs-area__unorganized-no-data"
            />
          </template>
          <template #item="{ element }">
            <OFSOrphan
              :element
              @delete="handleDeleteAsset"
            />
          </template>
        </draggable>
      </div>
    </div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, markRaw, inject, watch, nextTick } from 'vue'
import { cloneDeep, get, has, orderBy, set, unset } from 'lodash'
import draggable from 'vuedraggable'
import { useManualRefHistory } from '@vueuse/core'

import { AnalyticsField, TransactionSettings } from '@types'
import { OrganizeFamilyStructureAmountItem, OrganizeFamilyStructureItem, OrganizeFamilyStructurePath } from './utils/types'

import { METADATA_FIELDS } from './utils/const'
import { ASSET_FIELD, UNKNOWN_VALUE } from '@/const'

import { onCreateAsset } from '@/entities/transactions/components/utils/helpers'

import { useNotifications } from '@/plugins/notification'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesStore } from '@/store/repositories'

import { UILayoutNoData, UIPreloader, UITree } from '@ui/core'
import { UIButton } from '@ui/buttons'
import { ArrowUturnLeftIcon, ArrowUturnRightIcon, PlusIcon } from '@heroicons/vue/24/outline'

import PrecisionForm from './components/PrecisionForm.vue'
import AmountField from './components/AmountField.vue'
import OFSOrphan from './OrganizeFamilyStructureOrphan.vue'
import MoveTo from './components/MoveTo.vue'
import { handleCatchedError } from '@/helpers/common'

type OFSData = { tree: Record<string, OrganizeFamilyStructureItem>; orphans: string[] }

type Props = {
  paths: OrganizeFamilyStructurePath[]
  entrySettings?: TransactionSettings['entries'][number]
  accountTypes: string[]
  initialized: boolean
}

const props = defineProps<Props>()

const data = defineModel<OFSData>('data', { required: true })
const transactionDatesMap = defineModel<Map<string, string>>('dates', { required: true })

const { error, warn } = useNotifications()

const assetsBunchStore = useAssetsBunchStore()
const modalsStore = useModalsStore()
const repositoriesStore = useRepositoriesStore()

const historySource = computed({
  get: () => data.value,
  set: value => {
    data.value = value
  }
})

const { undo, redo, canUndo, canRedo, clear, commit } = useManualRefHistory(historySource, {
  dump: JSON.stringify,
  parse: JSON.parse
})

const nameFormatter = inject<(name: string) => string>('nameFormatter')

const chosenPath = ref<string[]>()
const scrollContainer = ref<HTMLDivElement>()
const expandedPaths = ref<Set<string>>(new Set())

const isRemoveAreaVisible = ref(false)

let historyCommitVersion = 0
let isHistoryCommitPending = false

const repoName = computed(() => repositoriesStore.getCurrentName)

const listData = computed(() => {
  const result = props.paths.map(path => {
    const value = `${path.path.at(-1)}`
    return {
      value,
      label: assetsBunchStore.getElementById(value)?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE,
      meta: { path: path.path }
    }
  })
  return orderBy(result, 'label')
})

const cancelPendingHistoryCommit = () => {
  historyCommitVersion += 1
  isHistoryCommitPending = false
}

const commitHistory = () => {
  if (!props.initialized || isHistoryCommitPending) return
  isHistoryCommitPending = true
  const version = ++historyCommitVersion

  nextTick(() => {
    if (version !== historyCommitVersion) return
    isHistoryCommitPending = false
    commit()
  })
}

const setHistoryBaseline = () => {
  cancelPendingHistoryCommit()
  commit()
  clear()
}

defineExpose({
  commitHistory,
})

const handleUndo = () => {
  cancelPendingHistoryCommit()
  undo()
}

const handleRedo = () => {
  cancelPendingHistoryCommit()
  redo()
}

// handle choose node in tree
const handleChoose = (path: string[]) => {
  chosenPath.value = path
}

// handle change in tree
const handleChange = (event: any, path: string[], open: () => void) => {
  if (event.added) {
    const targetAccountId = path.at(-1)
    const chosenAccountId = chosenPath.value?.at(-2)
    // if target account is the same as chosen account, do not add node
    if (targetAccountId === chosenAccountId) {
      chosenPath.value = undefined
      return
    }
    const element = event.added.element
    const assetId = Array.isArray(element) ? element.at(-1) : element
    handleAddNode(path, assetId)
    open()
  } else if (event.removed) {
    const element = event.removed.element
    handleRemovedNode(element)
  }
}

const addNodeToTargets = (
  tree: Record<string, OrganizeFamilyStructureItem>, 
  targetNode: OrganizeFamilyStructureItem, 
  accountId: string, 
  assetId: string, 
  amount: number
) => {
  // if account is repo root, add node to repo root
  if (accountId === AnalyticsField.REPO_ROOT) {
    const path = [AnalyticsField.REPO_ROOT, assetId]
    set(tree, path, {
      ...targetNode,
      path,
      amount,
    })
    return
  }
  // add node to all targets
  const targets = props.paths.filter(item => item.path.at(-1) === accountId)
  targets.forEach(target => {
    const path = [...target.path, assetId]
    set(tree, path, {
      ...targetNode,
      path,
      amount,
    })
  })
}

// handle add node to tree
const handleAddNode = (path: string[], assetId: string, callback?: (tree: Record<string, OrganizeFamilyStructureItem>) => void) => {
  const tree = cloneDeep(data.value.tree)
  const targetNode = get(tree, [...path, assetId])
  let amount = (targetNode?.amount ?? 0)
  const accountId = path.at(-1)
  if (!accountId) {
    error({ message: 'Account ID is required' })
    return
  }
  if (chosenPath.value) {
    // get asset id and account id
    if (!assetId) {
      error({ message: 'Asset ID is required' })
      return
    }
    if (assetId === accountId) {
      error({ message: 'You cannot add a node to itself' })
      chosenPath.value = undefined
      return
    }
    const chosenNode = get(tree, chosenPath.value)
    if (!chosenNode) return
    // if target exists, sum amounts
    amount += (chosenNode.amount ?? 0)
    addNodeToTargets(tree, chosenNode, accountId, assetId, amount)
  } else {
    // if orphan node is added, set amount to 1
    amount = 1
    addNodeToTargets(tree, {}, accountId, assetId, amount)
  }
  callback?.(tree)
  data.value.tree = tree
  commitHistory()
  // if orphan node is added, check precision amount
  if (!chosenPath.value && path.length > 1) {
    nextTick(() => {
      checkPrecisionAmount(assetId)
    })
  }
}

const removeNodeFromTargets = (tree: Record<string, OrganizeFamilyStructureItem>, accountId: string, assetId: string) => {
  // remove node from all targets
  const targets = props.paths.filter(item => item.path.at(-1) === assetId && item.path.at(-2) === accountId)
  targets.forEach(target => {
    unset(tree, target.path)
  })
}

// handle removed node from tree
const handleRemovedNode = (path: string[]) => {
  if (!chosenPath.value) return
  const tree = cloneDeep(data.value.tree)
  chosenPath.value = undefined
  const assetId = path.at(-1)
  const accountId = path.at(-2)
  if (!assetId || !accountId) {
    error({ message: 'Asset ID and account ID are required' })
    return
  }
  removeNodeFromTargets(tree, accountId, assetId)
  data.value.tree = tree
  commitHistory()
}

const isPathWithinPath = (path: string[], targetPath: string[]) => {
  return targetPath.every((item, index) => path[index] === item)
}

// Collect the removed node and every nested child asset from the current tree branch.
const getNodeAssetIds = (node: OrganizeFamilyStructureItem, path: string[]) => {
  const assetIds: string[] = []
  const assetId = path.at(-1)
  if (assetId) {
    assetIds.push(assetId)
  }

  Object.entries(node).forEach(([key, value]) => {
    if (METADATA_FIELDS.includes(key) || !value || typeof value !== 'object' || Array.isArray(value)) return
    assetIds.push(...getNodeAssetIds(value as OrganizeFamilyStructureItem, [...path, key]))
  })

  return assetIds
}

// Assets used in another branch should stay organized there and not be added to orphans.
const hasAssetOutsidePath = (path: string[]) => {
  const assetId = path.at(-1)
  const accountId = path.at(-2)
  if (!assetId || !accountId) {
    return
  }
  return props.paths.some(item => item.path.at(-1) === assetId && item.path.at(-2) !== accountId && !isPathWithinPath(item.path, path))
}

const addOrphan = (tree: Record<string, OrganizeFamilyStructureItem>, orphans: string[], path: string[], checkRelatives = true, allowWarnings = true) => {
  const node = get(tree, path)
  if (!node) {
    error({ message: 'Node was not found' })
    return
  }
  const assetId = path.at(-1)
  const accountId = path.at(-2)
  if (!assetId || !accountId) {
    error({ message: 'Asset ID and account ID are required' })
    return
  }
  // Moving a parent to orphans removes its whole subtree, so orphan eligible descendants too.
  getNodeAssetIds(node, path).forEach(childAssetId => {
    // If this asset still exists elsewhere in the tree, it should not become unorganized.
    const isAssetOutsidePath = checkRelatives && hasAssetOutsidePath(path)
    if (isAssetOutsidePath || orphans.includes(childAssetId)) {
      if (childAssetId === assetId && isAssetOutsidePath && allowWarnings) {
        warn({ message: 'Asset is in few nodes, please remove it from all nodes if you want to make it unorganized' })
      }
      return
    }
    orphans.push(childAssetId)
  })
}

// if orphan asset was moved from unorganized to tree
const handleOrphansChange = (event: any) => {
  chosenPath.value = undefined
  const tree = cloneDeep(data.value.tree)
  let orphans = cloneDeep(data.value.orphans)
  if (event.added) {
    const path = event.added.element
    addOrphan(tree, orphans, path)
    const assetId = path.at(-1)
    const accountId = path.at(-2)
    if (!assetId || !accountId) {
      error({ message: 'Asset ID and account ID are required' })
      return
    }
    removeNodeFromTargets(tree, accountId, assetId)
  } else if (event.removed) {
    const assetId = event.removed.element
    // remove asset id from orphans
    orphans = orphans.filter(id => id !== assetId)
  }
  data.value.tree = tree
  data.value.orphans = orphans
  commitHistory()
}

const handleClickAmount = (path: string[]) => {
  // get asset id and account id
  const assetId = path.at(-1)
  const accountId = path.at(-2)
  if (!assetId || !accountId) {
    error({ message: 'Asset ID and account ID are required' })
    return
  }
  checkPrecisionAmount(assetId)
}

const handleAddGroup = () => {
  const restrictions = props.accountTypes
  const instance = assetsBunchStore.createElement()
  onCreateAsset(
    '',
    { restrictions, hideEODSearch: true, equalizeTicker: true, title: 'Add Owner/Group' },
    instance,
    modalsStore,
    (assetId: string) => {
      const orphans = cloneDeep(data.value.orphans)
      orphans.unshift(assetId)
      data.value.orphans = orphans
      commitHistory()
    }
  )
}

const handleMoveTo = (target: string, path: string[]) => {
  chosenPath.value = path
  const assetId = path.at(-1)
  const accountId = path.at(-2)
  if (!assetId || !accountId) {
    error({ message: 'Asset ID and account ID are required' })
    return
  }
  const newPath = props.paths.find(item => item.path.at(-1) === target)?.path
  if (!newPath) {
    error({ message: 'Target path was not found' })
    return
  }
  handleAddNode(newPath, assetId, tree => {
    removeNodeFromTargets(tree, accountId, assetId)
  })
  chosenPath.value = undefined
}

const checkPrecisionAmount = (assetId: string) => {
  // open amount form
  const modalInstance = modalsStore.init('precision-form', markRaw(PrecisionForm))
  modalInstance?.open(modalsStore.getZIndex(), {
    entrySettings: props.entrySettings,
    assetId,
    paths: props.paths,
    data: listData,
    dates: transactionDatesMap.value,
    onComplete: (items: OrganizeFamilyStructureAmountItem[], date: string) => {
      const tree = cloneDeep(data.value.tree)
      const orphans = cloneDeep(data.value.orphans)
      const totalAmount = items.reduce((acc, item) => acc + (item.amount ?? 0), 0)
      props.paths
        .filter(item => items.some(({ accountId }) => accountId === item.path.at(-1)))
        .forEach(item => {
          const accountId = item.path.at(-1)
          if (!accountId) return
          const path = [...item.path, assetId]
          const amount = items.find(item => item.accountId === accountId)?.amount
          if (has(tree, path)) {
            if (!amount) {
              // add orphan
              addOrphan(tree, orphans, path, totalAmount > 0, false)
              // remove node from targets
              removeNodeFromTargets(tree, accountId, assetId)
            } else {
              set(tree, [...path, 'amount'], amount)
            }
          } else if (amount) {
            const sourcePath = props.paths.find(item => item.path.at(-1) === assetId)
            const sourceNode = sourcePath ? get(tree, sourcePath.path) : undefined
            addNodeToTargets(tree, sourceNode || { path }, accountId, assetId, amount)
          }
        })
      // the form's single date applies to every transaction this edit produces
      items.forEach(({ accountId }) => {
        if (!accountId) return
        transactionDatesMap.value.set(`${accountId}-${assetId}`, date)
      })
      data.value.orphans = orphans
      data.value.tree = tree
      commitHistory()
    }
  })
}

const handleDeleteAsset = async (assetId: string, callback: () => void) => {
  try {
    await assetsBunchStore.getElementById(assetId)?.destroy()
    data.value.orphans = data.value.orphans.filter(id => id !== assetId)
    setHistoryBaseline()
    callback()
  } catch (error) {
    handleCatchedError(error as string, { id: assetId })
    callback()
  }
}

watch(() => props.initialized, (value, prev) => {
  if (value && !prev) {
    setHistoryBaseline()
  }
}, { immediate: true })
</script>

<style scoped>
.ofs-area {
  &__tree {
    @apply flex-auto;
    @apply pt-1 sm:pt-2;

    :deep(.ui-tree) {
      @apply h-full;
    }

    :deep(.ui-tree__name) {
      @apply relative;
      @apply flex-auto;
    }

    :deep(.ui-tree__item) {
      @apply min-h-10;
      @apply justify-between;
      @apply pl-0.5 pr-1;
      @apply bg-body-gray hover:bg-body;

      &:has(.sortable-ghost) {
        @apply bg-indigo-50 dark:bg-gray-900;
      }

      &:has(.sortable-ghost) {
        .ofs-area__tree-item-placeholder {
          @apply block;
        }
        .ofs-area__tree-item-name {
          @apply hidden;
        }
        .ofs-orphan {
          @apply hidden;
        }
      }
    }
  }

  &__tree-actions {
    @apply flex items-center gap-2;
  }

  &__tree-item-placeholder {
    @apply hidden last:block;
    @apply last:opacity-50;
  }

  &__tree-item-name {
    @apply cursor-move;
  }

  &__unorganized {
    @apply h-1/2;
    @apply flex flex-col gap-2;
  }

  &__unorganized-area {
    @apply relative;
    @apply overflow-y-auto;
  }

  &__unorganized-list {
    @apply grid grid-cols-1 lg:grid-cols-2 gap-1.5;

    &--no-data {
      @apply flex-auto;
    }
  }

  &__unorganized-remove-area {
    @apply sm:col-span-2;
    @apply flex items-center justify-center;
    @apply py-1.5 px-2.5 gap-2;
    @apply rounded-md;
    @apply border border-dashed border-red-500 dark:border-red-300;
    @apply text-sm text-red-700 dark:text-red-300;
  }

  &__unorganized-no-data {
    @apply sm:col-span-2;
    @apply flex flex-auto items-center justify-center;
  }
}
</style>
